// Cloudflare Turnstile bot check for the three unauthenticated, email-sending
// `/api` calls (Signup, RequestPasswordReset, ResendVerification). The token
// rides the `x-turnstile-token` header; public-api decides what a missing or
// bad token means (off / log / enforce). The client therefore never blocks:
// no site key, script blocked, widget error or a slow challenge all resolve
// to `undefined` and the request goes out without the header.
import { env } from '@/env'

export const TURNSTILE_HEADER = 'x-turnstile-token'
export const TURNSTILE_TIMEOUT_MS = 5_000

/**
 * Turnstile `action` per endpoint. public-api rejects a token whose action does
 * not match the endpoint it was sent to, so each flow gets its own widget.
 */
export const TURNSTILE_ACTIONS = {
  signup: 'signup',
  passwordReset: 'password_reset',
  resendVerification: 'resend_verification',
} as const

export type TurnstileAction = (typeof TURNSTILE_ACTIONS)[keyof typeof TURNSTILE_ACTIONS]

const SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit'

export interface TurnstileRenderOptions {
  sitekey: string
  action: TurnstileAction
  execution: 'execute'
  appearance: 'interaction-only'
  callback: (token: string) => void
  'error-callback': () => void
  'expired-callback': () => void
  'timeout-callback': () => void
}

export interface TurnstileApi {
  render: (container: HTMLElement, options: TurnstileRenderOptions) => string | undefined
  execute: (widgetId: string) => void
  reset: (widgetId: string) => void
}

declare global {
  interface Window {
    turnstile?: TurnstileApi
  }
}

export interface TurnstileDeps {
  siteKey: string | undefined
  action: TurnstileAction
  loadApi: () => Promise<TurnstileApi>
  createContainer: () => HTMLElement
  timeoutMs?: number
}

/**
 * Returns a function producing one fresh token per call, or `undefined` when
 * Turnstile is unavailable or does not answer within the timeout. One widget
 * per action is rendered lazily and reset after every attempt so tokens are never reused.
 */
export function createTurnstileTokenGetter(deps: TurnstileDeps) {
  const timeoutMs = deps.timeoutMs ?? TURNSTILE_TIMEOUT_MS
  let widget: Promise<{ api: TurnstileApi; id: string }> | undefined
  let pending: ((token: string | undefined) => void) | undefined

  const settle = (token: string | undefined) => {
    const resolve = pending
    pending = undefined
    resolve?.(token)
  }

  const getWidget = () => {
    widget ??= deps.loadApi().then((api) => {
      const id = api.render(deps.createContainer(), {
        sitekey: deps.siteKey!,
        action: deps.action,
        execution: 'execute',
        appearance: 'interaction-only',
        callback: (token) => settle(token),
        'error-callback': () => settle(undefined),
        'expired-callback': () => settle(undefined),
        'timeout-callback': () => settle(undefined),
      })
      if (!id) throw new Error('turnstile render failed')
      return { api, id }
    })
    // A failed load is retried on the next submit rather than cached forever.
    widget.catch(() => {
      widget = undefined
    })
    return widget
  }

  return async function getTurnstileToken(): Promise<string | undefined> {
    if (!deps.siteKey) return undefined
    // A previous attempt still in flight (double submit) gets no token.
    settle(undefined)

    let current: { api: TurnstileApi; id: string } | undefined
    let timer: ReturnType<typeof setTimeout> | undefined
    let finish: (token: string | undefined) => void = () => {}
    const token = new Promise<string | undefined>((resolve) => {
      finish = (t) => {
        if (pending === finish) pending = undefined
        resolve(t)
      }
      pending = finish
      timer = setTimeout(() => finish(undefined), timeoutMs)
    })

    try {
      current = await Promise.race([getWidget(), token.then(() => undefined)])
      if (current && pending === finish) current.api.execute(current.id)
      else finish(undefined)
    } catch {
      finish(undefined)
    }

    const result = await token
    clearTimeout(timer)
    try {
      // Leave the widget alone if a newer attempt already took it over.
      if (pending === undefined) current?.api.reset(current.id)
    } catch {
      // reset failing only means the next execute may error; that also falls back.
    }
    return result
  }
}

function loadTurnstileScript(): Promise<TurnstileApi> {
  if (window.turnstile) return Promise.resolve(window.turnstile)
  return new Promise((resolve, reject) => {
    const script = document.createElement('script')
    script.src = SCRIPT_SRC
    script.async = true
    script.onload = () =>
      window.turnstile ? resolve(window.turnstile) : reject(new Error('turnstile missing'))
    script.onerror = () => {
      script.remove()
      reject(new Error('turnstile script failed to load'))
    }
    document.head.appendChild(script)
  })
}

function createWidgetContainer(): HTMLElement {
  // Stays empty unless Cloudflare decides it needs an interactive challenge.
  const el = document.createElement('div')
  el.style.cssText = 'position:fixed;bottom:16px;right:16px;z-index:2147483647'
  document.body.appendChild(el)
  return el
}

const getters = new Map<TurnstileAction, () => Promise<string | undefined>>()

/** One fresh token for `action`, or `undefined` when Turnstile is unavailable. */
export function getTurnstileToken(action: TurnstileAction): Promise<string | undefined> {
  let get = getters.get(action)
  if (!get) {
    get = createTurnstileTokenGetter({
      siteKey: typeof window === 'undefined' ? undefined : env.NEXT_PUBLIC_TURNSTILE_SITE_KEY,
      action,
      loadApi: loadTurnstileScript,
      createContainer: createWidgetContainer,
    })
    getters.set(action, get)
  }
  return get()
}

/** Axios request config adding the token header when there is one. */
export function turnstileHeaders(token: string | undefined) {
  return token ? { headers: { [TURNSTILE_HEADER]: token } } : undefined
}
